import { InputAdornment } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { IMaskMixin } from 'react-imask'
import { IPositionType } from '../generated-types'
import { AppTextField } from './AppTextField'
import { CoordinateDirection, getCoordinateDirectionMarker } from './CoordinateFormField'
import { calculateDmsToGps, gpsToDmsInput, isSameDisplayedCoordinate } from './GpsToDms'

const MaskedStyledInput = IMaskMixin(({ inputRef, endAdornment, ...otherProps }) => (
  <AppTextField
    {...(otherProps as any)}
    inputRef={inputRef}
    fullWidth
    InputProps={{ disableUnderline: true, endAdornment }}
  />
))

const getDmsMask = (direction: CoordinateDirection) =>
  direction === 'longitude'
    ? [{ mask: '0`0`0`\u00B00`0.`0`0`0`0`0`0' }, { mask: '-0`0`0`\u00B00`0.`0`0`0`0`0`0' }]
    : [{ mask: '0`0`\u00B00`0.`0`0`0`0`0`0' }, { mask: '-0`0`\u00B00`0.`0`0`0`0`0`0' }]

export const DMSLocationTextField = ({ disabled, direction, form, report, onAccept, value }) => {
  const { t } = useTranslation(['common'])
  const marker = getCoordinateDirectionMarker(value, direction === 'Longitude' ? 'longitude' : 'latitude', t)
  return (
    <MaskedStyledInput
      mask={getDmsMask(direction === 'Longitude' ? 'longitude' : 'latitude')}
      placeholderChar="0"
      lazy={false}
      value={value}
      unmask={false}
      overwrite
      style={{
        display:
          (form.getValues('type') == IPositionType.MooringLine || form.getValues('type') == IPositionType.Corner) &&
          report?.allowEditPositionGPS
            ? 'block'
            : 'none',
      }}
      onAccept={onAccept}
      label={t(direction, { ns: 'common' })}
      disabled={disabled}
      endAdornment={marker ? <InputAdornment position="end">{marker}</InputAdornment> : undefined}
    />
  )
}

// Degrees/decimal minutes input (as in work assignments) bound to a form field holding decimal degrees
export const DmsCoordinateFormField: React.FC<{
  name: string
  direction: CoordinateDirection
  label: string
  disabled?: boolean
  showErrors?: boolean
}> = ({ name, direction, label, disabled, showErrors = false }) => {
  const { t } = useTranslation(['common'])
  const { field, fieldState } = useController({ name })
  const isLongitude = direction === 'longitude'
  const [text, setText] = useState(() => gpsToDmsInput(field.value, isLongitude))
  const isTyping = useRef(false)

  // Follow values set from outside the input, e.g. form.reset
  useEffect(() => {
    if (isTyping.current) return
    const current = field.value === '' || field.value == null ? null : Number(field.value)
    if (current == null || !isSameDisplayedCoordinate(text, current, isLongitude)) {
      setText(gpsToDmsInput(current as number, isLongitude))
    }
  }, [field.value])

  const marker = getCoordinateDirectionMarker(field.value, direction, t)

  return (
    <MaskedStyledInput
      mask={getDmsMask(direction)}
      placeholderChar="0"
      lazy={false}
      value={text}
      unmask={false}
      overwrite
      onFocus={() => {
        isTyping.current = true
      }}
      onAccept={(value, maskRef, e) => {
        setText(value)
        // Only user input updates the form, not the mask formatting the value it was given
        if (!e) return
        if (maskRef.unmaskedValue === '') {
          field.onChange(null)
        } else if (!isSameDisplayedCoordinate(value, field.value, isLongitude)) {
          field.onChange(calculateDmsToGps({ dmsValue: value }))
        }
      }}
      onBlur={e => {
        isTyping.current = false
        field.onBlur()
      }}
      label={label}
      disabled={disabled}
      error={(fieldState.isTouched || showErrors) && !!fieldState.error}
      helperText={fieldState.isTouched || showErrors ? fieldState.error?.message : undefined}
      endAdornment={marker ? <InputAdornment position="end">{marker}</InputAdornment> : undefined}
    />
  )
}
